import { useState, useEffect, useMemo, useCallback, type ChangeEvent } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { RootState } from '../../store';
import { editPlantThunk } from './CreatePlantSlice';
import {
  Asset,
  CustomerLite,
  EMPTY_PLANT_INFO,
  PlantInfo,
  Site,
  buildEditPayload,
  buildPlantInfoFromApiPlant,
  clearPlantStorage,
  collectPlantValidationErrors,
  extractEditErrorMessage,
  extractSaveErrorMessage,
  filterBySearchTerm,
  findCustomerByPlantData,
  hasPlantDataChanged as checkPlantDataChanged,
  parseJsonSafely,
  triggerPlantListRefresh,
} from './createPlantHelpers';
import { fetchCustomersList, loadPlantForEdit, removePlant, saveNewPlant } from './createPlantDataLoaders';

export function useCreatePlantForm() {
  const { plantId } = useParams<{ plantId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const plantInfoRedux = useAppSelector((state: RootState) => state.plantInfo.plantInfo);

  const [currentStep] = useState(1);
  const [mode, setMode] = useState<'create' | 'edit'>(plantId ? 'edit' : 'create');
  const [customers, setCustomers] = useState<CustomerLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerLite | null>(null);
  const [plantInfo, setPlantInfo] = useState<PlantInfo>(EMPTY_PLANT_INFO);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [plantData, setPlantData] = useState<Record<string, unknown> | null>(null);
  const [selectedPlantId, setSelectedPlantId] = useState<string | null>(null);
  const [originalPlantInfo, setOriginalPlantInfo] = useState<PlantInfo | null>(null);
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [customerSearchText, setCustomerSearchText] = useState('');
  const [isSiteDropdownOpen, setIsSiteDropdownOpen] = useState(false);
  const [siteSearchText, setSiteSearchText] = useState('');
  const [sites, setSites] = useState<Site[]>([]);
  const [coordinatesDisplay, setCoordinatesDisplay] = useState('');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [prevPlantId, setPrevPlantId] = useState<string | undefined>(plantId);

  const filteredCustomers = useMemo(
    () =>
      filterBySearchTerm(customers, customerSearchText, (customer) => [
        customer.customerName,
        customer.soldTo ?? '',
      ]),
    [customers, customerSearchText]
  );

  const filteredSites = useMemo(
    () =>
      filterBySearchTerm(sites, siteSearchText, (site) => [site.site, site.siteId]),
    [sites, siteSearchText]
  );

  const resetFormState = useCallback(() => {
    setPlantInfo(EMPTY_PLANT_INFO);
    setSelectedCustomer(null);
    setOriginalPlantInfo(null);
    setPlantData(null);
    setSites([]);
    setCoordinatesDisplay('');
    dispatch({ type: 'createPlant/resetPlantForm' });
  }, [dispatch]);

  const applyLoadedPlantState = useCallback(
    (state: {
      plantData: Record<string, unknown>;
      plantInfo: PlantInfo;
      assets: Asset[];
      selectedPlantId: string | null;
      selectedCustomer: CustomerLite | null;
      sites: Site[];
    }) => {
      setPlantData(state.plantData);
      setPlantInfo(state.plantInfo);
      setOriginalPlantInfo({ ...state.plantInfo });
      setAssets(state.assets);
      setSelectedPlantId(state.selectedPlantId);
      if (state.selectedCustomer) {
        setSelectedCustomer(state.selectedCustomer);
      }
      if (state.sites.length > 0) {
        setSites(state.sites);
      }
    },
    []
  );

  useEffect(() => {
    if (plantInfo.latitude && plantInfo.longitude) {
      setCoordinatesDisplay(`${plantInfo.latitude}, ${plantInfo.longitude}`);
    } else {
      setCoordinatesDisplay('');
    }
  }, [plantInfo.latitude, plantInfo.longitude]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target?.closest('.searchable-select-container')) {
        setIsCustomerDropdownOpen(false);
        setIsSiteDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (prevPlantId === plantId) {
      setMode(plantId ? 'edit' : 'create');
      if (!plantId) {
        clearPlantStorage();
        resetFormState();
      }
      return;
    }

    if (prevPlantId && plantId && prevPlantId !== plantId) {
      clearPlantStorage();
      resetFormState();
    }

    setPrevPlantId(plantId);
    setMode(plantId ? 'edit' : 'create');

    if (!plantId) {
      clearPlantStorage();
      resetFormState();
    }
  }, [plantId, prevPlantId, resetFormState]);

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setError(null);

      try {
        const validCustomers = await fetchCustomersList();
        setCustomers(validCustomers);

        if (!plantId) {
          return;
        }

        const result = await loadPlantForEdit(plantId, validCustomers);
        if (result.state) {
          applyLoadedPlantState(result.state);
        } else if (result.error) {
          setError(result.error);
        }
      } catch {
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    void fetchInitialData();
  }, [plantId, applyLoadedPlantState]);

  useEffect(() => {
    if (!plantId || !plantData) {
      return;
    }

    const plantInfoData = buildPlantInfoFromApiPlant({
      customer: String(plantData.customer ?? ''),
      site: String(plantData.site ?? ''),
      plant: String(plantData.plant ?? ''),
      status: String(plantData.status ?? 'Active'),
      latitude: plantData.latitude as string | null | undefined,
      longitude: plantData.longitude as string | null | undefined,
    });

    dispatch({ type: 'createPlant/setPlantInfo', payload: plantInfoData });
    setPlantInfo(plantInfoData);
    setOriginalPlantInfo(plantInfoData);

    const customer = findCustomerByPlantData(
      {
        customerId: plantData.customerId as string | undefined,
        customer: plantData.customer as string | undefined,
      },
      customers
    );

    if (customer) {
      setSelectedCustomer(customer);
      setSites(customer.sites?.length ? customer.sites : []);
    }
  }, [plantId, plantData, customers, dispatch]);

  useEffect(() => {
    if (!selectedCustomer) {
      return;
    }

    dispatch({
      type: 'plant/fetchSites/fulfilled',
      payload: selectedCustomer.sites ?? [],
    });
  }, [selectedCustomer, dispatch]);

  useEffect(() => {
    if (!plantInfoRedux || mode !== 'edit' || plantInfo.plantName) {
      return;
    }

    setPlantInfo({
      ...plantInfoRedux,
      latitude: (plantInfoRedux as PlantInfo & { latitude?: string }).latitude ?? '',
      longitude: (plantInfoRedux as PlantInfo & { longitude?: string }).longitude ?? '',
    });

    const reduxCustomerId = (plantInfoRedux as PlantInfo & { customerId?: string }).customerId;
    if (reduxCustomerId) {
      const customer = customers.find((entry) => entry.customerId === reduxCustomerId);
      if (customer) {
        setSelectedCustomer(customer);
      }
    }
  }, [plantInfoRedux, customers, mode, plantInfo.plantName]);

  useEffect(() => {
    if (mode !== 'create') {
      return;
    }

    const savedFormData = localStorage.getItem('createPlantFormData');
    if (!savedFormData) {
      return;
    }

    const parsedData = parseJsonSafely<{
      plantInfo: PlantInfo;
      selectedCustomer?: CustomerLite;
    }>(savedFormData);

    if (!parsedData) {
      return;
    }

    setPlantInfo(parsedData.plantInfo);
    if (parsedData.selectedCustomer) {
      setSelectedCustomer(parsedData.selectedCustomer);
      if (parsedData.selectedCustomer.sites?.length) {
        setSites(parsedData.selectedCustomer.sites);
      }
    }
  }, [mode]);

  useEffect(() => {
    return () => {
      const currentPath = globalThis.location.pathname;
      if (!currentPath.includes('create-plant-details') && !currentPath.includes('create-plant')) {
        localStorage.removeItem('createPlantFormData');
        dispatch({ type: 'createPlant/resetPlantForm' });
      }
    };
  }, [dispatch]);

  useEffect(() => {
    if (!location.state?.resetForm) {
      return;
    }

    resetFormState();
    setSelectedCustomer(null);
    localStorage.removeItem('createPlantFormData');

    if (globalThis.history.replaceState) {
      globalThis.history.replaceState(
        { ...location.state, resetForm: undefined },
        document.title,
        location.pathname
      );
    }
  }, [location, dispatch, resetFormState]);

  useEffect(() => {
    if (!plantId || !plantInfo.plantName) {
      return;
    }

    const currentState = globalThis.history.state ?? {};
    globalThis.history.replaceState(
      {
        ...currentState,
        plantName: plantInfo.plantName,
        breadcrumbTitle: plantInfo.plantName,
      },
      '',
      `${globalThis.location.pathname}${globalThis.location.search}`
    );
  }, [plantId, plantInfo.plantName]);

  const updatePlantInfo = useCallback(
    (updatedInfo: PlantInfo) => {
      setPlantInfo(updatedInfo);
      dispatch({ type: 'createPlant/setPlantInfo', payload: updatedInfo });
    },
    [dispatch]
  );

  const handleCustomerSelect = (customer: CustomerLite) => {
    setSelectedCustomer(customer);
    const updatedInfo = {
      ...plantInfo,
      customer: customer.customerId,
      site: '',
    };
    setPlantInfo(updatedInfo);
    dispatch({
      type: 'createPlant/setPlantInfo',
      payload: {
        ...updatedInfo,
        customerId: customer.customerId || '',
        customerName: customer.customerName || '',
      },
    });
    setIsSiteDropdownOpen(false);
    setSiteSearchText('');
    setSites(customer.sites?.length ? customer.sites : []);
    setIsCustomerDropdownOpen(false);
    setCustomerSearchText('');
  };

  const handleSiteSelect = (site: Site) => {
    const updatedInfo = {
      ...plantInfo,
      site: site.site,
      latitude: site.latitude || plantInfo.latitude,
      longitude: site.longitude || plantInfo.longitude,
    };
    setPlantInfo(updatedInfo);
    dispatch({
      type: 'createPlant/setPlantInfo',
      payload: {
        ...updatedInfo,
        siteId: site.siteId || '',
        siteName: site.site,
      },
    });
    setIsSiteDropdownOpen(false);
    setSiteSearchText('');
  };

  const handleMapLocationSelect = (lat: number, lng: number) => {
    updatePlantInfo({
      ...plantInfo,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6),
    });
  };

  const handleCoordinatesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCoordinatesDisplay(value);

    const coords = value.split(',').map((coord) => coord.trim());
    if (coords.length === 2) {
      const lat = Number.parseFloat(coords[0]);
      const lng = Number.parseFloat(coords[1]);

      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        updatePlantInfo({
          ...plantInfo,
          latitude: lat.toString(),
          longitude: lng.toString(),
        });
      }
      return;
    }

    if (value === '') {
      updatePlantInfo({
        ...plantInfo,
        latitude: '',
        longitude: '',
      });
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;

    if (name === 'customer' || name === 'site') {
      return;
    }

    if (name === 'plantName' || name === 'status' || name === 'latitude' || name === 'longitude') {
      updatePlantInfo({
        ...plantInfo,
        [name]: value,
      } as PlantInfo);
    }
  };

  const hasPlantDataChanged = () =>
    checkPlantDataChanged(originalPlantInfo, plantInfo, mode);

  const getSelectedSite = () =>
    sites.find((site) => site.site.trim().toLowerCase() === plantInfo.site.trim().toLowerCase());

  const navigateToDetails = (delay = 1200, state?: { plantId?: string }) => {
    setTimeout(() => navigate('/create-plant-details', state ? { state } : undefined), delay);
  };

  const handleEditSave = async () => {
    if (selectedCustomer?.customerId) {
      localStorage.setItem('customerid', selectedCustomer.customerId);
    }

    if (!hasPlantDataChanged()) {
      navigate('/create-plant-details');
      return;
    }

    if (!selectedCustomer) {
      throw new Error('Please select a customer');
    }

    const selectedSite = sites.find((site) => site.site === plantInfo.site);
    if (!selectedSite) {
      throw new Error('Please select a valid site');
    }

    const result = await dispatch(
      editPlantThunk(
        buildEditPayload(selectedPlantId, plantData, selectedCustomer, selectedSite, plantInfo)
      )
    );

    if (editPlantThunk.fulfilled.match(result)) {
      setOriginalPlantInfo({ ...plantInfo });
      toast.success('Plant updated successfully');
      navigateToDetails();
      return;
    }

    const errorData = result.payload as { data?: { Message?: string } } | undefined;
    if (errorData?.data?.Message?.includes('already exists')) {
      setOriginalPlantInfo({ ...plantInfo });
      toast.success('Plant coordinates updated successfully');
      navigateToDetails();
      return;
    }

    throw new Error(errorData?.data?.Message || 'Failed to update plant');
  };

  const handleCreateSave = async () => {
    const selectedSite = getSelectedSite();
    const plantDataToStore = {
      customerId: selectedCustomer?.customerId || '',
      customerName: selectedCustomer?.customerName || '',
      siteId: selectedSite?.siteId || '',
      siteName: selectedSite?.site || '',
      plantName: plantInfo.plantName,
      plantStatus: plantInfo.status,
      assets: (plantData?.assets as Asset[]) || [],
    };

    const response = await saveNewPlant({
      customerName: selectedCustomer?.customerName || '',
      siteName: selectedSite?.site || '',
      plantName: plantInfo.plantName,
      plantStatus: plantInfo.status,
      cnxSiteId: Number.parseInt(selectedSite?.siteId || '0', 10),
      latitude: plantInfo.latitude || '0',
      longitude: plantInfo.longitude || '0',
    });

    if (!response.Success || !response.Data) {
      const errorMessage = response.Message || 'Failed to create plant';
      setError(errorMessage);
      toast.error(errorMessage);
      return;
    }

    const updatedPlantDataToStore = {
      ...plantDataToStore,
      plantId: response.Data,
      id: response.Data,
    };

    localStorage.setItem('currentplantguid', response.Data);
    localStorage.setItem('currentPlantId', response.Data);
    localStorage.setItem('currentPlantData', JSON.stringify(updatedPlantDataToStore));
    localStorage.setItem('customerid', plantDataToStore.customerId || '');
    triggerPlantListRefresh();
    toast.success('Plant Saved Successfully');
    navigateToDetails();
  };

  const handleSaveAndNext = async () => {
    const validationErrors = collectPlantValidationErrors(mode, plantInfo, selectedCustomer);
    if (validationErrors.length > 0) {
      toast.error(validationErrors.join(', '));
      return;
    }

    try {
      setLoading(true);
      localStorage.setItem(
        'createPlantFormData',
        JSON.stringify({ plantInfo, selectedCustomer })
      );

      if (plantId) {
        await handleEditSave();
        return;
      }

      await handleCreateSave();
    } catch (err) {
      const errorMessage = extractSaveErrorMessage(err as Parameters<typeof extractSaveErrorMessage>[0]);
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (selectedCustomer?.customerId) {
      localStorage.setItem('customerid', selectedCustomer.customerId);
    }

    if (mode === 'edit' && !hasPlantDataChanged()) {
      navigate('/create-plant-details', { state: { plantId: plantData?.id as string | undefined } });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (!selectedCustomer) {
        throw new Error('Please select a customer');
      }

      const selectedSite = sites.find((site) => site.site === plantInfo.site);
      if (!selectedSite) {
        throw new Error('Please select a valid site');
      }

      const result = await dispatch(
        editPlantThunk(
          buildEditPayload(selectedPlantId, plantData, selectedCustomer, selectedSite, plantInfo)
        )
      );

      if (editPlantThunk.fulfilled.match(result)) {
        setOriginalPlantInfo({ ...plantInfo });
        toast.success('Plant Saved Successfully');
        navigateToDetails(1200, { plantId: plantData?.id as string | undefined });
        return;
      }

      if (editPlantThunk.rejected.match(result)) {
        toast.error(extractEditErrorMessage(result.payload));
      }
    } catch (err) {
      toast.error(extractEditErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlant = () => {
    if (!plantId || !plantData) {
      toast.error('No plant data available to delete');
      return;
    }
    setShowDeleteModal(true);
  };

  const confirmDeletePlant = async () => {
    if (!plantId) {
      return;
    }

    try {
      setDeleting(true);
      setError(null);
      const response = await removePlant(plantId);

      if (response.data?.Success) {
        setShowDeleteModal(false);
        globalThis.scrollTo(0, 0);
        navigate('/plants', {
          state: { plantDeleted: true, message: 'Plant deleted successfully!' },
        });
        return;
      }

      const errorMessage = response.data?.Message || 'Failed to delete plant';
      setError(errorMessage);
      toast.error(`Error: ${errorMessage}`);
    } catch {
      const errorMessage = 'Failed to delete plant. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const cancelDeletePlant = () => {
    setShowDeleteModal(false);
  };

  const handleCancel = () => {
    dispatch({ type: 'createPlant/resetPlantForm' });
    globalThis.scrollTo(0, 0);
    navigate('/plants');
  };

  const handleTabNavigation = (tabNumber: number) => {
    const plantGuid = localStorage.getItem('currentplantguid');
    globalThis.scrollTo({ top: 0, behavior: 'smooth' });

    if (tabNumber === 2) {
      if (plantGuid || (mode === 'edit' && plantId)) {
        navigate('/create-plant-details');
      } else {
        setError('Please save the plant details first before proceeding to asset hierarchy.');
      }
      return;
    }

    if (tabNumber === 3) {
      if (plantGuid || (mode === 'edit' && plantId)) {
        navigate('/create-plant-details/notification');
      } else {
        setError('Please save the plant details first before proceeding to notification setup.');
      }
    }
  };

  const toggleCustomerDropdown = () => {
    const next = !isCustomerDropdownOpen;
    setIsCustomerDropdownOpen(next);
    setIsSiteDropdownOpen(false);
    if (!next) {
      setCustomerSearchText('');
    }
  };

  const toggleSiteDropdown = () => {
    const next = !isSiteDropdownOpen;
    setIsSiteDropdownOpen(next);
    setIsCustomerDropdownOpen(false);
    if (!next) {
      setSiteSearchText('');
    }
  };

  return {
    plantId,
    currentStep,
    mode,
    loading,
    error,
    deleting,
    showDeleteModal,
    plantData,
    plantInfo,
    coordinatesDisplay,
    assets,
    selectedCustomer,
    filteredCustomers,
    filteredSites,
    isCustomerDropdownOpen,
    isSiteDropdownOpen,
    customerSearchText,
    siteSearchText,
    hasPlantDataChanged,
    handleSaveAndNext,
    handleNext,
    handleDeletePlant,
    confirmDeletePlant,
    cancelDeletePlant,
    handleCancel,
    handleTabNavigation,
    handleInputChange,
    handleCoordinatesChange,
    handleMapLocationSelect,
    handleCustomerSelect,
    handleSiteSelect,
    toggleCustomerDropdown,
    toggleSiteDropdown,
    setCustomerSearchText,
    setIsCustomerDropdownOpen,
    setSiteSearchText,
    setIsSiteDropdownOpen,
  };
}

export type CreatePlantFormState = ReturnType<typeof useCreatePlantForm>;
